import { Globe, Eye, EyeOff } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import type { SharingMode } from '../../redux/types/charterTypes'

interface SharingModeSelectorProps {
  currentMode: SharingMode
  onChange: (mode: SharingMode) => void
  disabled?: boolean
}

const MODES: {
  key: SharingMode
  label: string
  description: string
  icon: React.ReactNode
  color: string
}[] = [
  {
    key: 'auto',
    label: 'Auto',
    description: 'AI coach shares what it thinks is most helpful for the team.',
    icon: <Globe className='h-4 w-4' />,
    color: 'bg-blue-50 border-blue-300 text-blue-700',
  },
  {
    key: 'selective',
    label: 'Selective',
    description: 'Choose specific sections to share with your team.',
    icon: <Eye className='h-4 w-4' />,
    color: 'bg-amber-50 border-amber-300 text-amber-700',
  },
  {
    key: 'manual',
    label: 'Private',
    description: 'Nothing is shared. Only you see your prep profile.',
    icon: <EyeOff className='h-4 w-4' />,
    color: 'bg-gray-50 border-gray-300 text-gray-700',
  },
]

export function SharingModeSelector({
  currentMode,
  onChange,
  disabled = false,
}: SharingModeSelectorProps) {
  return (
    <TooltipProvider>
      <div className='space-y-2'>
        <p className='text-xs font-medium text-gray-500 uppercase tracking-wide'>
          Sharing Mode
        </p>
        <div className='flex gap-2'>
          {MODES.map(({ key, label, description, icon, color }) => (
            <Tooltip key={key}>
              <TooltipTrigger asChild>
                <button
                  disabled={disabled}
                  onClick={() => onChange(key)}
                  className={cn(
                    'flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-all',
                    currentMode === key
                      ? color + ' ring-2 ring-offset-1 ring-current'
                      : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50',
                    disabled && 'opacity-50 cursor-not-allowed'
                  )}
                  aria-pressed={currentMode === key}
                >
                  {icon}
                  {label}
                </button>
              </TooltipTrigger>
              <TooltipContent side='bottom' className='max-w-xs text-sm'>
                {description}
              </TooltipContent>
            </Tooltip>
          ))}
        </div>
        {currentMode === 'selective' && (
          <Badge variant='outline' className='text-xs text-amber-600 border-amber-300'>
            Select sections below to share
          </Badge>
        )}
      </div>
    </TooltipProvider>
  )
}
